/**
 * Step 3 — Bring your history (optional, skippable).
 * Drag-drop .txt/.zip upload with required timezone picker, collection picker,
 * and 422-ambiguity handling.
 *
 * Collection picker (ruling 2): defaults to the wizard-created group so imported
 * history lands alongside live messages. "New collection" creates a standalone
 * collection instead (original behaviour, for archives without a live group).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, FileText, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dropzone } from '@/components/patterns/dropzone';
import { IconTile } from '@/components/patterns/icon-tile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select } from '@/components/ui/select';
import { connectorApi, type ImportAmbiguityError, type ImportBatch, importsApi } from '@/lib/api';
import { IANA_TIMEZONES } from '@/lib/constants';
import { qk } from '@/lib/query-client';
import { browserTimezone, formatBytes } from '@/lib/utils';

const POLL_INTERVAL_MS = 2_000;

/** Sentinel value for the "create a new standalone collection" option. */
const NEW_COLLECTION = '__new__';

interface Props {
  /** Pre-fills the new-collection title when no group exists. */
  defaultCollectionTitle?: string;
  onNext: () => void;
  onSkip: () => void;
}

export function StepImport({ defaultCollectionTitle = '', onNext, onSkip }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [timezone, setTimezone] = useState(browserTimezone());
  const [ambiguity, setAmbiguity] = useState<ImportAmbiguityError | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);

  // Collection picker — default to wizard-created group, fall back to standalone.
  const [selectedTarget, setSelectedTarget] = useState<string>(NEW_COLLECTION);
  const [newTitle, setNewTitle] = useState(defaultCollectionTitle);

  // Fetch live groups so the picker stays in sync with linked WhatsApp groups.
  const { data: groups } = useQuery({
    queryKey: qk.groups,
    queryFn: () => connectorApi.listGroups(),
  });

  // Once groups load, default-select the first group if the user hasn't picked yet.
  useEffect(() => {
    const first = groups?.[0];
    if (first && selectedTarget === NEW_COLLECTION) {
      setSelectedTarget(first.id);
    }
  }, [groups, selectedTarget]);

  // Live progress polling once a batch is created.
  const { data: batch } = useQuery({
    queryKey: qk.import(batchId ?? ''),
    queryFn: () => importsApi.get(batchId as string),
    enabled: !!batchId,
    refetchInterval: (query) => {
      const data = query.state.data as ImportBatch | undefined;
      if (!data) return POLL_INTERVAL_MS;
      return data.status === 'parsing' || data.status === 'indexing' ? POLL_INTERVAL_MS : false;
    },
  });

  const upload = useMutation({
    mutationFn: ({
      f,
      tz,
      title,
      dateOrder,
      collectionId,
    }: {
      f: File;
      tz: string;
      title: string;
      dateOrder?: 'dmy' | 'mdy';
      collectionId?: string;
    }) => importsApi.upload(f, tz, title, dateOrder, collectionId),
    onSuccess: (res) => {
      if ('ambiguity' in res) {
        setAmbiguity(res.ambiguity ?? null);
      } else {
        setBatchId(res.result.batchId);
        setAmbiguity(null);
      }
    },
    onError: (err) => {
      // Surface the server's reason (e.g. unrecognized_format) when present.
      const msg = (err as { message?: string })?.message;
      toast.error(msg && msg.length > 3 ? msg : 'Upload failed. Please try again.');
    },
  });

  function pickFile(f: File) {
    const ok = f.name.toLowerCase().endsWith('.txt') || f.name.toLowerCase().endsWith('.zip');
    if (!ok) {
      toast.error('Only .txt and .zip files are supported');
      return;
    }
    setFile(f);
    setAmbiguity(null);
    setBatchId(null);
    // Pre-fill new-collection title from filename when user hasn't typed one.
    if (!newTitle) setNewTitle(f.name.replace(/\.(txt|zip)$/i, ''));
  }

  function handleUpload(dateOrder?: 'dmy' | 'mdy') {
    if (!file) return;
    const isExistingGroup = selectedTarget !== NEW_COLLECTION;
    upload.mutate({
      f: file,
      tz: timezone,
      // collectionTitle is only used when creating a standalone collection.
      title: newTitle || file.name.replace(/\.(txt|zip)$/i, ''),
      dateOrder,
      collectionId: isExistingGroup ? selectedTarget : undefined,
    });
  }

  const started = batchId !== null;
  const isFailed = batch?.status === 'failed';
  const counts = batch?.counts;

  const progressPct =
    counts && counts.parsed > 0 ? Math.round((counts.imported / counts.parsed) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="neutral">
          <Upload className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Bring your chat history</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Export your existing WhatsApp group chat (Settings → Chats → Export Chat) and upload it
          here. This step is optional — skip it if you&apos;re starting fresh.
        </p>

        <div className="mt-6 space-y-4">
          {/* Collection picker — ruling 2: import into live group by default */}
          <div className="space-y-1.5">
            <Label htmlFor="import-target">Import into</Label>
            <Select
              id="import-target"
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
            >
              {groups && groups.length > 0 ? (
                <>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.title}
                    </option>
                  ))}
                  <option value={NEW_COLLECTION}>New standalone collection…</option>
                </>
              ) : (
                <option value={NEW_COLLECTION}>New collection</option>
              )}
            </Select>
            {selectedTarget === NEW_COLLECTION && (
              <div className="mt-2 space-y-1.5">
                <Label htmlFor="collection-title">Collection name</Label>
                <Input
                  id="collection-title"
                  placeholder={file?.name.replace(/\.(txt|zip)$/i, '') ?? 'My chat history'}
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Timezone picker — required per §5.6 */}
          <div className="space-y-1.5">
            <Label htmlFor="tz">
              Timezone{' '}
              <span className="text-xs text-muted-foreground">
                (required — exports don&apos;t include it)
              </span>
            </Label>
            <Select id="tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {IANA_TIMEZONES.length === 0 ? (
                <option value={timezone}>{timezone}</option>
              ) : (
                IANA_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))
              )}
            </Select>
          </div>

          {/* Drop zone */}
          {!file ? (
            <Dropzone onFile={pickFile} />
          ) : (
            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setFile(null);
                  setAmbiguity(null);
                  setBatchId(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* 422 ambiguity resolution */}
          {ambiguity && (
            <Alert variant="warning">
              <AlertTitle>Date format ambiguous</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>
                  Brain couldn&apos;t determine whether dates in this file are day/month/year or
                  month/day/year. Please pick the correct interpretation:
                </p>
                <div className="space-y-2">
                  {(ambiguity.interpretations ?? []).map((interp) => (
                    <Button
                      key={interp.dateOrder}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleUpload(interp.dateOrder)}
                      disabled={upload.isPending}
                    >
                      <span className="font-mono text-xs">
                        {interp.dateOrder === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
                      </span>
                      <span className="ml-2 text-muted-foreground">— e.g. {interp.sample}</span>
                    </Button>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Import status — non-blocking: keeps running server-side once started. */}
          {started && (
            <div className="space-y-2 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  {!batch || batch.status === 'parsing'
                    ? 'Importing your history…'
                    : batch.status === 'indexing'
                      ? 'Indexing messages…'
                      : batch.status === 'done'
                        ? 'History imported'
                        : 'Import failed'}
                </span>
                {!batch || batch.status === 'parsing' || batch.status === 'indexing' ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : batch.status === 'done' ? (
                  <Check className="h-4 w-4 text-success" />
                ) : null}
              </div>

              {(!batch || batch.status === 'parsing' || batch.status === 'indexing') && (
                <p className="text-xs text-faint">
                  This keeps running in the background — you can continue setup and track progress
                  on your dashboard and Sources.
                </p>
              )}

              {counts && (
                <>
                  <Progress value={progressPct} />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>Parsed: {counts.parsed.toLocaleString()}</span>
                    <span>Imported: {counts.imported.toLocaleString()}</span>
                    <span>Duplicates skipped: {counts.deduped.toLocaleString()}</span>
                    {counts.media_staged !== undefined && (
                      <span>Media staged: {counts.media_staged}</span>
                    )}
                  </div>
                </>
              )}

              {isFailed && batch?.error && (
                <p className="text-xs text-destructive">{batch.error}</p>
              )}
            </div>
          )}

          {!started && (
            <Button
              size="cta"
              className="w-full"
              disabled={!file || upload.isPending}
              onClick={() => handleUpload()}
            >
              {upload.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                'Upload and import'
              )}
            </Button>
          )}
        </div>
      </div>

      {started && !isFailed ? (
        <Button size="cta" className="w-full" onClick={onNext}>
          Continue — connect your brain
        </Button>
      ) : (
        <button
          type="button"
          onClick={onSkip}
          className="w-full py-2 text-center text-[13px] text-faint transition-colors hover:text-foreground"
        >
          Skip — I&apos;ll do this later
        </button>
      )}
    </div>
  );
}
